import React, { useEffect, useState } from "react";
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import moment from 'moment';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const REPORT_COLUMN_COUNT = 8;

function VisitRow({ visit }) {
    const [open, setOpen] = useState(false);

    const excludedServices = visit.services.filter(service => service.excluded);
    const excludedAmount = excludedServices.reduce((sum, service) => sum + service.amount, 0);
    const countedAmount = Math.max(visit.total_amount - excludedAmount, 0);

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell sx={{ width: 40, px: 0.5 }}>
                    <IconButton size="small" onClick={() => setOpen(!open)} aria-label={open ? "Свернуть" : "Подробная информация"}>
                        {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {moment(visit.visit_date).format("DD.MM.YYYY")}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.75 }}>
                        {moment(visit.visit_date).format("HH:mm")}
                    </Typography>
                </TableCell>
                <TableCell>{visit.client_name}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{visit.car_number}</TableCell>
                <TableCell>{visit.complex_name}</TableCell>
                <TableCell>{visit.administrator_name}</TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {visit.services.map((service, idx) => (
                            <Chip
                                key={idx}
                                label={service.name}
                                size="small"
                                variant={service.excluded ? "outlined" : "filled"}
                                color={service.excluded ? "default" : "primary"}
                                title={service.excluded ? "не входит в средний чек" : undefined}
                            />
                        ))}
                    </Stack>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
                        <span>{visit.total_amount.toLocaleString('ru-RU')} ₽</span>
                        {visit.excluded_from_average && (
                            <Tooltip title={`Клиент заплатил ${visit.total_amount.toLocaleString('ru-RU')} ₽ — сумма реальна. В средний чек не идёт, т.к. все услуги визита не учитываются в среднем чеке`}>
                                <Box
                                    component="span"
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        bgcolor: 'warning.main',
                                        display: 'inline-block',
                                        flexShrink: 0
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Stack>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={REPORT_COLUMN_COUNT}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ my: 2, ml: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxWidth: 480 }}>
                                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}>
                                    Подробная информация — {visit.client_name}, {moment(visit.visit_date).format("DD.MM.YYYY")}
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Позиция</TableCell>
                                            <TableCell align="right">Сумма</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visit.services.map((service, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell sx={service.excluded ? { color: 'text.disabled', textDecoration: 'line-through' } : undefined}>
                                                    {service.name}
                                                    {service.excluded && (
                                                        <Chip
                                                            label="не в средний чек"
                                                            size="small"
                                                            variant="outlined"
                                                            color="warning"
                                                            sx={{ ml: 1, height: 18, '& .MuiChip-label': { fontSize: 10, px: 0.75 } }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell align="right" sx={service.excluded ? { color: 'warning.main' } : undefined}>
                                                    {service.amount.toLocaleString('ru-RU')} ₽
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <Stack spacing={0.5} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">Итого по визиту</Typography>
                                        <Typography variant="body2">{visit.total_amount.toLocaleString('ru-RU')} ₽</Typography>
                                    </Stack>
                                    {excludedAmount > 0 && (
                                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                                            <Typography variant="body2" color="text.secondary">
                                                Исключено ({excludedServices.map(s => s.name).join(', ')})
                                            </Typography>
                                            <Typography variant="body2" color="warning.main" sx={{ whiteSpace: 'nowrap' }}>
                                                − {excludedAmount.toLocaleString('ru-RU')} ₽
                                            </Typography>
                                        </Stack>
                                    )}
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Учтено в среднем чеке</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }} color="primary.dark">
                                            {countedAmount.toLocaleString('ru-RU')} ₽
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function MonthlyReport() {
    const api_url = process.env.REACT_APP_API_URL;
    const complexName = localStorage.getItem('complex_name');

    const [startDate, setStartDate] = useState(moment().startOf('month').format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [complexFilter, setComplexFilter] = useState('all');
    const [administratorId, setAdministratorId] = useState('any');
    const [clientName, setClientName] = useState('');
    const [carNumber, setCarNumber] = useState('');
    const [includeGoods, setIncludeGoods] = useState(true);

    const [availableAdmins, setAvailableAdmins] = useState([]);
    const [report, setReport] = useState([]);
    const [averageBill, setAverageBill] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        async function getAvailableAdmins() {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
                body: JSON.stringify({ complex_name: complexName })
            };
            const response = await fetch(api_url + "/api/availableAdmins", requestOptions);
            const body = await response.json();
            setAvailableAdmins(body);
        }
        getAvailableAdmins();
    }, [api_url, complexName]);

    useEffect(() => {
        async function getMonthlyReport() {
            setIsLoading(true);
            try {
                const requestOptions = {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
                    body: JSON.stringify({
                        start_date: startDate,
                        end_date: endDate,
                        complex_name: complexFilter,
                        administrator_id: administratorId,
                        client_name: clientName,
                        car_number: carNumber,
                        include_goods: includeGoods
                    })
                };
                const response = await fetch(api_url + "/api/monthlyReport", requestOptions);
                const body = await response.json();
                setReport(Array.isArray(body?.visits) ? body.visits : []);
                setAverageBill(body?.average_bill || 0);
            } catch (error) {
                console.error("Failed to fetch monthly report", error);
            }
            setIsLoading(false);
        }
        getMonthlyReport();
    }, [api_url, startDate, endDate, complexFilter, administratorId, clientName, carNumber, includeGoods]);

    async function handleExport() {
        setIsExporting(true);
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
                body: JSON.stringify({
                    start_date: startDate,
                    end_date: endDate,
                    complex_name: complexFilter,
                    administrator_id: administratorId,
                    client_name: clientName,
                    car_number: carNumber,
                    include_goods: includeGoods,
                    format: 'csv'
                })
            };
            const response = await fetch(api_url + "/api/monthlyReport", requestOptions);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `visits-report-${startDate}-${endDate}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to export monthly report", error);
        }
        setIsExporting(false);
    }

    const totalSum = report.reduce((sum, visit) => sum + (visit.total_amount || 0), 0);
    const notInAverageCount = report.filter(visit => visit.excluded_from_average).length;

    const stats = [
        { label: "Визитов", value: report.length },
        { label: "Сумма визитов", value: `${totalSum.toLocaleString('ru-RU')} ₽` },
        { label: "Средний чек", value: `${averageBill.toLocaleString('ru-RU')} ₽` },
        { label: "Не в среднем чеке", value: notInAverageCount },
    ];

    return (
        <>
            <head>
                <title>Отчёт по визитам</title>
                <meta property="og:title" content="Отчёт по визитам" key="title" />
            </head>

            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
                    Отчёт по визитам
                </Typography>

                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                    <Box display="flex" flexWrap="wrap" alignItems="flex-end" gap={2}>
                        <TextField
                            id="start_date"
                            label="Дата с"
                            type="date"
                            value={startDate}
                            sx={{ width: 160 }}
                            InputLabelProps={{ shrink: true }}
                            onChange={e => setStartDate(e.target.value)}
                        />

                        <TextField
                            id="end_date"
                            label="Дата по"
                            type="date"
                            value={endDate}
                            sx={{ width: 160 }}
                            InputLabelProps={{ shrink: true }}
                            onChange={e => setEndDate(e.target.value)}
                        />

                        {complexName === "Офис" && (
                            <FormControl sx={{ width: 160 }}>
                                <InputLabel id="complex-filter-label">Филиал</InputLabel>
                                <Select
                                    labelId="complex-filter-label"
                                    value={complexFilter}
                                    label="Филиал"
                                    onChange={e => setComplexFilter(e.target.value)}
                                >
                                    <MenuItem value="all">Все филиалы</MenuItem>
                                    <MenuItem value="Кудрово">Кудрово</MenuItem>
                                    <MenuItem value="Охта">Охта</MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        <FormControl sx={{ width: 200 }}>
                            <InputLabel id="admin-filter-label">Администратор</InputLabel>
                            <Select
                                labelId="admin-filter-label"
                                value={administratorId}
                                label="Администратор"
                                onChange={e => setAdministratorId(e.target.value)}
                            >
                                <MenuItem value="any">Все администраторы</MenuItem>
                                {availableAdmins.map(admin => (
                                    <MenuItem key={admin.user_id} value={admin.user_id}>{admin.fullname}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            sx={{ width: 200 }}
                            label="Клиент"
                            placeholder="Любой"
                            value={clientName}
                            onChange={e => setClientName(e.target.value)}
                        />

                        <TextField
                            sx={{ width: 160 }}
                            label="Номер авто"
                            placeholder="Любой"
                            value={carNumber}
                            onChange={e => setCarNumber(e.target.value)}
                        />

                        <ToggleButtonGroup
                            exclusive
                            sx={{ height: 56 }}
                            value={includeGoods ? 'all' : 'services'}
                            onChange={(e, value) => {
                                if (value !== null) setIncludeGoods(value === 'all');
                            }}
                        >
                            <ToggleButton value="all">Все визиты</ToggleButton>
                            <ToggleButton value="services">Только услуги</ToggleButton>
                        </ToggleButtonGroup>

                        <Button
                            variant="contained"
                            disabled={isExporting || isLoading}
                            onClick={handleExport}
                        >
                            {isExporting ? "Экспорт..." : "Скачать CSV"}
                        </Button>
                    </Box>
                    {!includeGoods && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Показаны только визиты с оплаченными услугами (как в разделе «Мотивация»): визиты без услуг исключены,
                            сумма считается по стоимости услуг без учёта товаров.
                        </Typography>
                    )}
                </Paper>

                <Box
                    display="grid"
                    gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))"
                    gap={2}
                    mb={3}
                >
                    {stats.map(stat => (
                        <Paper key={stat.label} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {stat.label}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                                {stat.value}
                            </Typography>
                        </Paper>
                    ))}
                </Box>

                <Stack direction="row" spacing={2.5} flexWrap="wrap" alignItems="center" sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Chip label="услуга" size="small" variant="filled" color="primary" />
                        <Typography variant="caption" color="text.secondary">— учитывается в среднем чеке</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Chip label="услуга" size="small" variant="outlined" color="default" />
                        <Typography variant="caption" color="text.secondary">— не учитывается в среднем чеке</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box
                            component="span"
                            sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'warning.main', display: 'inline-block' }}
                        />
                        <Typography variant="caption" color="text.secondary">— сумма визита не учитывается в среднем чеке</Typography>
                    </Stack>
                </Stack>

                {isLoading ? (
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                ) : report.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="body1" color="text.secondary">
                            Нет визитов по заданным фильтрам
                        </Typography>
                    </Paper>
                ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640, borderRadius: 2 }}>
                        <Table stickyHeader aria-label="monthly report table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 40, px: 0.5 }} />
                                    <TableCell>Дата</TableCell>
                                    <TableCell>Клиент</TableCell>
                                    <TableCell>Авто</TableCell>
                                    <TableCell>Комплекс</TableCell>
                                    <TableCell>Администратор</TableCell>
                                    <TableCell>Услуги</TableCell>
                                    <TableCell align="right">Сумма визита</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.map(visit => (
                                    <VisitRow key={visit.visit_id} visit={visit} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Container>
        </>
    );
}
